import { useState } from 'react';
import MathRenderer from '../../../components/base/MathRenderer';

interface Formula {
  label: string;
  displayLatex: string;
  example: string;
  description: string;
}

interface Category {
  id: string;
  label: string;
  icon: string;
  color: string;
  accent: string;
  formulas: Formula[];
}

const CATEGORIES: Category[] = [
  {
    id: 'derivatives',
    label: 'Derivatives',
    icon: 'ri-arrow-right-up-line',
    color: 'from-violet-500 to-fuchsia-500',
    accent: 'bg-violet-50 border-violet-200 text-violet-700',
    formulas: [
      { label: 'Power Rule', displayLatex: '$\\frac{d}{dx}(x^n) = nx^{n-1}$', example: 'derivative of x^5', description: 'Differentiate any power of x' },
      { label: 'Chain Rule', displayLatex: '$\\frac{d}{dx}[f(g(x))] = f\'(g(x))\\cdot g\'(x)$', example: 'derivative of sin(x^2)', description: 'Composite function differentiation' },
      { label: 'Product Rule', displayLatex: '$(fg)\' = f\'g + fg\'$', example: 'derivative of x^2 * sin(x)', description: 'Differentiate a product' },
      { label: 'Quotient Rule', displayLatex: '$\\left(\\frac{f}{g}\\right)\' = \\frac{f\'g - fg\'}{g^2}$', example: 'derivative of sin(x)/x', description: 'Differentiate a quotient' },
      { label: 'd/dx sin(x)', displayLatex: '$\\frac{d}{dx}\\sin(x) = \\cos(x)$', example: 'derivative of sin(3x)', description: 'Sine derivative' },
      { label: 'd/dx cos(x)', displayLatex: '$\\frac{d}{dx}\\cos(x) = -\\sin(x)$', example: 'derivative of cos(x^2)', description: 'Cosine derivative' },
      { label: 'd/dx eˣ', displayLatex: '$\\frac{d}{dx}e^x = e^x$', example: 'derivative of e^(2x)', description: 'Exponential derivative' },
      { label: 'd/dx ln(x)', displayLatex: '$\\frac{d}{dx}\\ln(x) = \\frac{1}{x}$', example: 'derivative of ln(x^2 + 1)', description: 'Natural log derivative' },
      { label: 'd/dx arctan(x)', displayLatex: '$\\frac{d}{dx}\\arctan(x) = \\frac{1}{1+x^2}$', example: 'derivative of atan(x)', description: 'Arctan derivative' },
      { label: 'd/dx xⁿ (General)', displayLatex: '$\\frac{d}{dx}(ax^n) = anx^{n-1}$', example: 'derivative of 3x^4 - 2x^2 + 7', description: 'Polynomial differentiation' },
    ],
  },
  {
    id: 'integrals',
    label: 'Integrals',
    icon: 'ri-function-line',
    color: 'from-emerald-500 to-teal-500',
    accent: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    formulas: [
      { label: 'Power Rule', displayLatex: '$\\int x^n\\,dx = \\frac{x^{n+1}}{n+1} + C$', example: 'integrate x^4 dx', description: 'Integrate any power' },
      { label: '∫ sin(x)', displayLatex: '$\\int \\sin(x)\\,dx = -\\cos(x) + C$', example: 'integrate sin(x) dx', description: 'Sine integral' },
      { label: '∫ cos(x)', displayLatex: '$\\int \\cos(x)\\,dx = \\sin(x) + C$', example: 'integrate cos(x) dx', description: 'Cosine integral' },
      { label: '∫ eˣ', displayLatex: '$\\int e^x\\,dx = e^x + C$', example: 'integrate e^x dx', description: 'Exponential integral' },
      { label: '∫ 1/x', displayLatex: '$\\int \\frac{1}{x}\\,dx = \\ln|x| + C$', example: 'integrate 1/x from 1 to e', description: 'Log integral' },
      { label: '∫ 1/(1+x²)', displayLatex: '$\\int \\frac{1}{1+x^2}\\,dx = \\arctan(x) + C$', example: 'integrate 1/(1+x^2) dx', description: 'Arctan integral' },
      { label: 'Definite Integral', displayLatex: '$\\int_a^b f(x)\\,dx = F(b) - F(a)$', example: 'integrate x^2 from 0 to 3', description: 'Fundamental theorem' },
      { label: '∫ ln(x)', displayLatex: '$\\int \\ln(x)\\,dx = x\\ln(x) - x + C$', example: 'integrate ln(x) from 1 to 2', description: 'Log by parts' },
      { label: '∫ √x', displayLatex: '$\\int \\sqrt{x}\\,dx = \\frac{2}{3}x^{3/2} + C$', example: 'integrate sqrt(x) from 0 to 4', description: 'Square root integral' },
      { label: '∫ sin²(x)', displayLatex: '$\\int \\sin^2(x)\\,dx = \\frac{x}{2} - \\frac{\\sin(2x)}{4} + C$', example: 'integrate sin(x)^2 dx', description: 'Reduction formula' },
    ],
  },
  {
    id: 'trig',
    label: 'Trig',
    icon: 'ri-compass-3-line',
    color: 'from-orange-500 to-amber-500',
    accent: 'bg-orange-50 border-orange-200 text-orange-700',
    formulas: [
      { label: 'Pythagorean', displayLatex: '$\\sin^2(x) + \\cos^2(x) = 1$', example: 'simplify sin(x)^2 + cos(x)^2', description: 'Fundamental identity' },
      { label: 'tan definition', displayLatex: '$\\tan(x) = \\frac{\\sin(x)}{\\cos(x)}$', example: 'derivative of tan(x)', description: 'Tangent identity' },
      { label: 'Double Angle sin', displayLatex: '$\\sin(2x) = 2\\sin(x)\\cos(x)$', example: 'simplify 2*sin(x)*cos(x)', description: 'Double angle identity' },
      { label: 'Double Angle cos', displayLatex: '$\\cos(2x) = \\cos^2(x) - \\sin^2(x)$', example: 'simplify cos(x)^2 - sin(x)^2', description: 'Double angle identity' },
      { label: 'Sum sin', displayLatex: '$\\sin(a\\pm b) = \\sin a\\cos b \\pm \\cos a\\sin b$', example: 'derivative of sin(x + pi/4)', description: 'Angle addition' },
      { label: 'Sum cos', displayLatex: '$\\cos(a\\pm b) = \\cos a\\cos b \\mp \\sin a\\sin b$', example: 'derivative of cos(x + pi/3)', description: 'Angle addition' },
      { label: 'sec & csc', displayLatex: '$\\sec(x) = \\frac{1}{\\cos(x)},\\;\\csc(x) = \\frac{1}{\\sin(x)}$', example: 'derivative of sec(x)', description: 'Reciprocal trig' },
      { label: 'Half Angle sin²', displayLatex: '$\\sin^2(x) = \\frac{1-\\cos(2x)}{2}$', example: 'integrate sin(x)^2 from 0 to pi', description: 'Half-angle formula' },
      { label: 'Euler\'s Formula', displayLatex: '$e^{ix} = \\cos(x) + i\\sin(x)$', example: 'plot sin(x) + cos(x)', description: 'Complex exponentials' },
      { label: 'Law of Cosines', displayLatex: '$c^2 = a^2 + b^2 - 2ab\\cos(C)$', example: 'sqrt(3^2 + 4^2 - 2*3*4*cos(pi/3))', description: 'Triangle sides' },
    ],
  },
  {
    id: 'algebra',
    label: 'Algebra',
    icon: 'ri-calculator-line',
    color: 'from-cyan-500 to-sky-500',
    accent: 'bg-cyan-50 border-cyan-200 text-cyan-700',
    formulas: [
      { label: 'Quadratic Formula', displayLatex: '$x = \\frac{-b \\pm \\sqrt{b^2-4ac}}{2a}$', example: 'solve 2x^2 + 3x - 5 = 0', description: 'Roots of ax²+bx+c=0' },
      { label: 'Square of Sum', displayLatex: '$(a+b)^2 = a^2 + 2ab + b^2$', example: 'expand (x + 3)^2', description: 'Binomial expansion' },
      { label: 'Square of Diff', displayLatex: '$(a-b)^2 = a^2 - 2ab + b^2$', example: 'expand (x - 4)^2', description: 'Binomial expansion' },
      { label: 'Diff of Squares', displayLatex: '$(a+b)(a-b) = a^2 - b^2$', example: 'factor x^2 - 9', description: 'Factoring pattern' },
      { label: 'Cube of Sum', displayLatex: '$(a+b)^3 = a^3+3a^2b+3ab^2+b^3$', example: 'expand (x + 1)^3', description: 'Cubic expansion' },
      { label: 'Sum of Cubes', displayLatex: '$a^3 + b^3 = (a+b)(a^2-ab+b^2)$', example: 'factor x^3 + 8', description: 'Factoring cubic' },
      { label: 'Geometric Series', displayLatex: '$\\sum_{k=0}^{n} r^k = \\frac{1-r^{n+1}}{1-r}$', example: 'solve x^3 - 6x^2 + 11x - 6 = 0', description: 'Sum formula' },
      { label: 'Logarithm Rules', displayLatex: '$\\log(ab) = \\log a + \\log b$', example: 'simplify log(x^2)', description: 'Log properties' },
      { label: 'Exponential', displayLatex: '$a^m \\cdot a^n = a^{m+n}$', example: 'simplify e^(2*x) * e^(3*x)', description: 'Exponent rules' },
      { label: 'AM-GM', displayLatex: '$\\frac{a+b}{2} \\geq \\sqrt{ab}$', example: 'Mean of {4, 9}', description: 'Arithmetic-geometric mean' },
    ],
  },
  {
    id: 'geometry',
    label: 'Geometry',
    icon: 'ri-shape-line',
    color: 'from-rose-500 to-pink-500',
    accent: 'bg-rose-50 border-rose-200 text-rose-700',
    formulas: [
      { label: 'Circle Area', displayLatex: '$A = \\pi r^2$', example: 'pi * 7^2', description: 'Area of a circle' },
      { label: 'Sphere Volume', displayLatex: '$V = \\frac{4}{3}\\pi r^3$', example: '(4/3) * pi * 5^3', description: 'Volume of a sphere' },
      { label: 'Pythagorean', displayLatex: '$a^2 + b^2 = c^2$', example: 'sqrt(3^2 + 4^2)', description: 'Right triangle hypotenuse' },
      { label: 'Cylinder Surface', displayLatex: '$A = 2\\pi r(r+h)$', example: '2 * pi * 3 * (3 + 10)', description: 'Surface area of cylinder' },
      { label: 'Cone Volume', displayLatex: '$V = \\frac{1}{3}\\pi r^2 h$', example: '(1/3) * pi * 4^2 * 9', description: 'Volume of a cone' },
      { label: 'Ellipse Area', displayLatex: '$A = \\pi ab$', example: 'pi * 5 * 3', description: 'Area of an ellipse' },
      { label: 'Arc Length', displayLatex: '$L = r\\theta$', example: '5 * pi/3', description: 'Arc length formula' },
      { label: 'Triangle Area', displayLatex: '$A = \\frac{1}{2}bh$', example: '(1/2) * 8 * 6', description: 'Area of a triangle' },
      { label: 'Distance', displayLatex: '$d = \\sqrt{(x_2-x_1)^2+(y_2-y_1)^2}$', example: 'sqrt((5-1)^2 + (7-4)^2)', description: '2D distance formula' },
      { label: 'Heron\'s Formula', displayLatex: '$A = \\sqrt{s(s-a)(s-b)(s-c)}$', example: 'sqrt(6*(6-3)*(6-4)*(6-5))', description: 'Triangle from 3 sides' },
    ],
  },
];

interface Props {
  show: boolean;
  onClose: () => void;
  onUse: (query: string) => void;
}

export default function FormulaReferencePanel({ show, onClose, onUse }: Props) {
  const [activeCategory, setActiveCategory] = useState('derivatives');
  const [search, setSearch] = useState('');

  const category = CATEGORIES.find(c => c.id === activeCategory) ?? CATEGORIES[0];

  const filtered = search.trim()
    ? CATEGORIES.flatMap(c => c.formulas.map(f => ({ ...f, catId: c.id })))
        .filter(f =>
          f.label.toLowerCase().includes(search.toLowerCase()) ||
          f.description.toLowerCase().includes(search.toLowerCase())
        )
    : category.formulas.map(f => ({ ...f, catId: activeCategory }));

  return (
    <>
      {/* Backdrop */}
      {show && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-[1px] z-40"
          onClick={onClose}
        />
      )}

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-[360px] bg-white border-l border-slate-200 z-50 flex flex-col transition-transform duration-300 ease-in-out ${
          show ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ boxShadow: '-8px 0 32px rgba(0,0,0,0.12)' }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-600 to-fuchsia-600 px-5 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-book-2-line text-white text-sm"></i>
            </div>
            <div>
              <h3 className="text-white font-bold text-sm">Formula Reference</h3>
              <p className="text-violet-200 text-xs">Click any formula to use it</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b border-slate-100 flex-shrink-0">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
            <input
              type="text"
              placeholder="Search formulas…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent"
            />
          </div>
        </div>

        {/* Category Tabs */}
        {!search && (
          <div className="flex overflow-x-auto border-b border-slate-100 flex-shrink-0"
            style={{ scrollbarWidth: 'none' }}>
            {CATEGORIES.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold transition-all cursor-pointer whitespace-nowrap border-b-2 ${
                  activeCategory === cat.id
                    ? 'border-violet-500 text-violet-700 bg-violet-50/50'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                <i className={`${cat.icon} text-xs`}></i>
                {cat.label}
              </button>
            ))}
          </div>
        )}

        {/* Formula List */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {search && filtered.length === 0 && (
            <div className="text-center py-10 text-slate-400 text-sm">
              <i className="ri-search-line text-2xl mb-2 block"></i>
              No formulas found for &ldquo;{search}&rdquo;
            </div>
          )}

          {filtered.map((formula, idx) => {
            const cat = CATEGORIES.find(c => c.id === (formula as any).catId) ?? category;
            return (
              <div
                key={idx}
                className="bg-white border border-slate-100 rounded-xl overflow-hidden hover:border-violet-200 transition-all group"
              >
                {/* Formula header */}
                <div className="px-3 pt-3 pb-1 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-5 h-5 rounded-md bg-gradient-to-br ${cat.color} flex items-center justify-center flex-shrink-0`}>
                      <i className={`${cat.icon} text-white`} style={{ fontSize: 9 }}></i>
                    </div>
                    <span className="text-xs font-semibold text-slate-700">{formula.label}</span>
                  </div>
                </div>

                {/* Formula display */}
                <div className="px-3 py-2 bg-slate-50 mx-3 mb-2 rounded-lg min-h-[36px] flex items-center">
                  <MathRenderer content={formula.displayLatex} className="text-sm text-slate-800 text-center w-full" />
                </div>

                {/* Description + Use button */}
                <div className="px-3 pb-3 flex items-center justify-between gap-2">
                  <p className="text-[10px] text-slate-400 leading-tight flex-1">{formula.description}</p>
                  <button
                    onClick={() => { onUse(formula.example); onClose(); }}
                    className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-[10px] font-bold rounded-lg hover:from-violet-600 hover:to-fuchsia-600 transition-all cursor-pointer whitespace-nowrap group-hover:shadow-sm"
                  >
                    <i className="ri-play-fill"></i>
                    Try it
                  </button>
                </div>

                {/* Example preview */}
                <div className="px-3 pb-2">
                  <code className="text-[9px] text-slate-400 font-mono bg-slate-50 px-2 py-0.5 rounded-md">
                    {formula.example}
                  </code>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 flex-shrink-0">
          <p className="text-[10px] text-slate-400 text-center">
            {CATEGORIES.reduce((a, c) => a + c.formulas.length, 0)} formulas across 5 categories
          </p>
        </div>
      </div>
    </>
  );
}
